import {
  RouteRecordRaw,
  createRouter,
  createWebHistory,
  RouteMeta,
  useRoute,
  RouteLocationNormalizedLoaded,
  RouteLocationNormalizedLoadedGeneric,
  Router,
  RouterView,
  START_LOCATION,
} from "vue-router";
import { DateTime } from "luxon";
import { useDataFiltersStore, applyShareParam } from "@/store/data_filters";
import { recordNavigationTarget } from "@/utils/staleChunk";
import { authRoutes } from "@/router/auth_routes";
import {
  organizationRoutes,
  publicDashboardRoute,
} from "@/router/organization_settings_routes";
import { websiteRoutes } from "@/router/website_routes";
import { publicReportRoute, reportingRoutes } from "@/router/reporting_routes";
import {
  aiRecommendationDetailRoute,
  insightsRoutes,
  recommendationTypeRoute,
} from "@/router/insights_routes";
import { marketingRoutes } from "@/router/marketing_routes";
import { commerceRoutes } from "@/router/commerce_routes";
import { toolsRoutes } from "@/router/tools_routes";
import { watch, computed, watchEffect } from "vue";
import { hasAccessTier, hasRequiredUserRole } from "@/utils/tiers";
import { publicBudgetRoute, salesRoutes } from "@/router/sales_routes";
import { useOrganizationQuery } from "@/services/organization";
import { useFeatureFlags } from "@/services/feature_flags";
import { useProfileQuery } from "@/services/user";
import { getLighthouseReportViewerLink } from "@/services/lighthouse";
import { useAuthStore } from "@/store/auth";
import { useRouteAccess } from "@/utils/routes";
import { backendEmailRoutes } from "@/router/backend_email_routes";
function devReferenceRoutes(): RouteRecordRaw[] {
  if (import.meta.env.MODE === "production") {
    return [];
  }
  return [
    {
      path: "icons",
      name: "icons",
      meta: { title: "Icons" },
      component: () => import("@/views/DevReferencePages/IconList.vue"),
    },
    {
      path: "sink",
      name: "sink",
      meta: { title: "Sink" },
      component: () => import("@/views/DevReferencePages/Sink.vue"),
    },
    {
      path: "lightbox",
      name: "lightbox_dev",
      meta: { title: "Lightbox" },
      component: () => import("@/views/DevReferencePages/LightboxTest.vue"),
    },
    {
      path: "sandbox",
      name: "sandbox",
      meta: { title: "Sandbox" },
      component: () => import("@/views/DevReferencePages/Sandbox.vue"),
      children: [
        {
          path: "open_graph_proxy",
          name: "open_graph_proxy",
          meta: { title: "Open Graph Proxy" },
          component: () =>
            import("@/views/DevReferencePages/Sandbox/OpenGraphProxy.vue"),
        },
        {
          path: "custom_data_import_available_entities",
          name: "custom_data_import_available_entities",
          meta: { title: "Custom Data Import - Available Entities" },
          component: () =>
            import("@/views/DevReferencePages/Sandbox/CustomDataImport/AvailableEntities.vue"),
        },
        {
          path: "custom_data_import_schemas",
          name: "custom_data_import_schemas",
          meta: { title: "Custom Data Import - Schemas" },
          component: () =>
            import("@/views/DevReferencePages/Sandbox/CustomDataImport/Schemas.vue"),
        },
      ],
    },
  ];
}

const oldRedirectRoutes: RouteRecordRaw[] = [
  {
    path: "marketing-performance/details",
    redirect: { name: "ad-performance-base" },
  },
  {
    path: "website-optimization",
    redirect: { name: "page-insights-search" },
    children: [
      {
        path: ":pk",
        redirect: { name: "page-insights-content" },
      },
    ],
  },
  {
    path: "organic-traffic",
    redirect: { name: "search-term-report" },
    children: [
      {
        path: ":id",
        redirect: { name: "search-term-detail" },
      },
    ],
  },
  {
    path: "competitor-monitor",
    redirect: { name: "competitor-overview" },
    children: [
      {
        path: "competitor-overview",
        redirect: { name: "competitor-overview" },
      },
      {
        path: "competitor-posts",
        redirect: { name: "competitor-posts" },
      },
    ],
  },
  {
    path: "website-performance",
    redirect: { name: "website-analytics" },
  },
  {
    path: "website-traffic",
    redirect: { name: "website-analytics" },
  },
  {
    path: "marketing",
    redirect: { name: "ad-performance" },
  },
  {
    path: "site-score/:pk",
    component: {
      setup() {
        const route = useRoute();
        window.location.replace(
          getLighthouseReportViewerLink(Number(route.params.pk)),
        );
      },
    },
  },
  {
    path: "performance-indicators",
    redirect: { name: "goals" },
  },
  {
    path: "ecommerce",
    redirect: { name: "commerce_ecom" },
  },
  {
    path: "/settings/resources",
    redirect: { name: "resources" },
  },
  {
    path: "/settings/merge-resources",
    redirect: { name: "merge-resources" },
  },
  {
    path: "/settings/merge-resources/edit/:pk",
    redirect: { name: "edit" },
  },
  {
    path: "/settings/add-resource",
    redirect: { name: "add-resource" },
  },
  {
    path: "/settings/add-resource/select-channel",
    redirect: { name: "select-channel" },
  },
  {
    path: "/settings/add-resource/xandr-auth",
    redirect: { name: "xandr-auth" },
  },
  {
    path: "marketing-performance",
    redirect: { name: "ad-performance-base" },
  },
  {
    path: "marketing-performance/overview",
    redirect: { name: "ad-performance-overview" },
  },
  {
    path: "marketing-performance/ads",
    redirect: { name: "marketing-performance-ads" },
  },
  {
    path: "content-lab",
    redirect: { name: "create-content" },
  },
  {
    path: "content-lab/content",
    redirect: { name: "content" },
  },
];

export const settingsRoute: RouteRecordRaw = {
  path: "settings",
  name: "settings",
  redirect: { name: "general" },
  meta: {
    title: "Settings",
    text: "Organization Settings",
    description: "Edit organization settings",
    icon: "cogs",
    helpDeskPath: "settings",
  },
  component: () =>
    import("@/views/OrganizationSettings/OrganizationOverview.vue"),
  children: [...organizationRoutes],
};

export const profileRoute: RouteRecordRaw = {
  path: "profile",
  name: "profile",
  meta: {
    title: "Profile",
    text: "Profile Settings",
    description: "Edit your user profile settings",
    icon: "user",
    helpDeskPath: "settings",
  },
  component: () => import("@/views/UserSettings/UserSettingsBase.vue"),
};

export const createOrganizationRoute: RouteRecordRaw = {
  path: "create-organization",
  name: "create-organization",
  meta: {
    title: "Create Organization",
    text: "Create a new organization",
    description: "Create a new organization, and invite users to it",
    icon: "plus",
    requiredPermissions: ["canOnboard"],
    helpDeskPath: "settings",
  },
  component: () =>
    import("@/views/OrganizationSettings/CreateOrganization.vue"),
};

export const agencyHubRoute: RouteRecordRaw = {
  path: "hub",
  name: "hub",
  meta: {
    title: "Hub",
    text: "Hub",
    description: "Overview of your organizations",
    icon: "users",
    requiredPermissions: ["isAgency"],
    beta: true,
  },
  component: () => import("@/views/Hub/HubView.vue"),
};

export const appDocsRoutes: RouteRecordRaw[] = [
  {
    path: "/docs",
    alias: ["/privacy"], // Preserved and redirects to keep existing links out in the wild working
    name: "docs",
    component: () => import("@/views/AppDocs/AppDocs.vue"),
    children: [
      {
        path: ":document",
        name: "docs-document",
        meta: { title: "Documentation" },
        component: () => import("@/views/AppDocs/AppDocs.vue"),
      },
    ],
  },
];

export const feedRoutes: RouteRecordRaw[] = [
  {
    path: "feed",
    name: "feed",
    meta: {
      title: "Feed",
      added: "2023-03-23",
      description:
        "Stay on top of the latest updates regarding your sales and marketing.",
      icon: "browser",
      requiredResources: [],
    },
    component: () => import("@/views/Feed/FeedView.vue"),
    children: [
      {
        path: "new",
        name: "feed-new-post",
        component: () => import("@/views/Feed/FeedView.vue"),
      },
    ],
  },
  {
    path: "feed/:id",
    name: "feed-post",
    meta: {
      title: "Feed Post",
      requiredResources: [],
      excludeFromNavbar: true,
    },
    component: () => import("@/views/Feed/FeedPostView.vue"),
  },
] as const;

// !!! IMPORTANT !!!
// Remember to change redirect URLs in the deployment repo envs if changing the root URL
const authedRoutes: RouteRecordRaw[] = [
  ...reportingRoutes,
  ...feedRoutes,
  ...websiteRoutes,
  ...marketingRoutes,
  ...commerceRoutes,
  ...insightsRoutes,
  ...toolsRoutes,
  ...salesRoutes,
  recommendationTypeRoute,
  aiRecommendationDetailRoute,
  // dashboardRoute,
  ...oldRedirectRoutes,
  ...devReferenceRoutes(),
  settingsRoute,
  profileRoute,
  agencyHubRoute,
  {
    path: "notifications",
    name: "notifications",
    meta: { title: "Notifications" },
    component: () => import("@/views/Notifications.vue"),
  },
  createOrganizationRoute,
];

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    name: "base",
    component: RouterView,
    children: [
      ...publicReportRoute,
      ...publicDashboardRoute,
      ...publicBudgetRoute,
      ...authRoutes.map((route) => ({
        ...route,
        meta: {
          ...(route.meta as RouteMeta),
          includeNavbar: false,
          containered: false,
        },
      })),
      ...authedRoutes.map((route) => ({
        ...route,
        meta: { ...(route.meta as RouteMeta), requiresAuth: true },
      })),
      ...appDocsRoutes,
      {
        path: ":pathMatch(.*)*",
        name: "not-found",
        props: (route) => ({
          resourceRequested: route.query.resourceRequested,
        }),
        meta: { title: "404 Not Found" },
        component: () => import("@/views/NotFound.vue"),
      },
      ...backendEmailRoutes(),
    ],
  },
];
const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.hash) {
      return { el: to.hash, behavior: "smooth" };
    }
    if (savedPosition) {
      return savedPosition;
    } else {
      // If the route name is the same, we only changed a parameter,
      // in which case we don't want to scroll to the top.
      if (from.name !== to.name) {
        return { top: 0 };
      } else {
        // But in marketing performance the same route is used for different "pages",
        // the only difference is the query parameter `level`.
        if (from.query.level !== to.query.level) {
          return { top: 0 };
        }
      }
    }
  },
});

// A stored homepage preference is only usable if it can actually be navigated
// to by name alone. `router.hasRoute()` is not enough: a route with path params
// (e.g. `budget_tool_detail`) is "known" but resolving it without an id throws
// `Missing required param`, which left users who had saved such a preference —
// the picker offered them until #8072 — unable to load any page, including the
// settings screen where they could change it back.
// Router-injectable so tests can drive the real logic against their own
// memory router instead of reimplementing it.
export function resolvableRouteName(
  r: Router,
  homeRoute: string | null | undefined,
): string | null {
  // Empty string must be rejected up front: `resolve({ name: "" })` does not
  // throw, it resolves to "/" — which routes straight back into this guard.
  if (!homeRoute) return null;
  try {
    // Resolve against START_LOCATION, not the current route: vue-router
    // inherits matching required params from wherever the user happens to be,
    // so on /budget-tool/123 a bare resolve would accept
    // `budget_tool_detail` and send the Home logo back to that same plan.
    // Throws both for an unknown name and for a known route whose path needs
    // params, which is the pair of failures a stored preference can hit.
    r.resolve({ name: homeRoute }, START_LOCATION);
    return homeRoute;
  } catch {
    return null;
  }
}

export function resolvableHomeRouteName(
  homeRoute: string | null | undefined,
): string | null {
  return resolvableRouteName(router, homeRoute);
}

// The home-route decision, separated from the guard's auth and profile-loading
// plumbing so it can be tested directly. The guard below is the only caller in
// production; keeping the choice here means a test cannot pass by exercising a
// copy of it.
export function homeRouteTargetName(
  r: Router,
  profile: {
    isError?: boolean;
    selectedHomeRoute?: string | null;
    hasAgencyHub?: boolean;
  },
): string {
  if (profile.isError) return "feed";
  // An explicit homepage preference always wins — including "hub" for users
  // who chose it (the picker only offers Hub to agency members, and the /hub
  // guard re-checks access).
  const stored = resolvableRouteName(r, profile.selectedHomeRoute);
  if (stored) return stored;
  // No usable preference: users with any enabled hub agency default to the
  // Hub. Uses hasAgencyHub so it stays consistent with the nav link and the
  // route guard.
  if (profile.hasAgencyHub) return "hub";
  return "feed";
}

// Where a "go to my homepage" action should land: the stored preference when
// it is usable, feed otherwise. Used by the Navbar logo, which must honour a
// Hub preference like any other.
export function homeRouteNameOrFeed(
  r: Router,
  storedHomeRoute: string | null | undefined,
): string {
  return resolvableRouteName(r, storedHomeRoute) ?? "feed";
}

// As above, but for the Hub's own exits — leaving the Hub must never land back
// on it, so a Hub preference falls through to feed there.
export function homeRouteNameAwayFromHub(
  r: Router,
  storedHomeRoute: string | null | undefined,
): string {
  const homeRoute = resolvableRouteName(r, storedHomeRoute);
  return homeRoute && homeRoute !== "hub" ? homeRoute : "feed";
}

export function useRequiresAuthNavigationGuard(): void {
  const authStore = useAuthStore();
  router.beforeEach((to) => {
    if (to.matched.some((record) => record.meta.requiresAuth)) {
      // this route requires auth, check if logged in
      // if not, redirect to login page.
      if (!authStore.isAuthed) {
        return {
          name: "login",
          query: { redirect: to.fullPath },
        };
      }
    }
    return;
  });
}

export function useHomeRouteRedirectGuard(): void {
  const authStore = useAuthStore();
  const profile = useProfileQuery();

  router.beforeEach(async (to) => {
    // Block non-agency users from accessing the hub route before the view loads
    if (to.name === "hub" && authStore.isAuthed) {
      if (profile.isLoading) {
        await new Promise<void>((resolve) => {
          const unwatch = watch(
            () => profile.isLoading,
            (loading) => {
              if (!loading) {
                unwatch();
                resolve();
              }
            },
            { immediate: true },
          );
        });
      }

      // If query errored, allow navigation (hub view will handle error state)
      if (profile.isError) {
        return;
      }

      // Hub access follows hasAgencyHub — true when the user has at least one
      // flag-enabled member agency, matching the backend (which serves the hub
      // if ANY member agency is enabled, not only the home agency). Selection-
      // independent, so /hub stays reachable from any org.
      if (!profile.data?.hasAgencyHub) {
        return { name: "feed" };
      }
    }

    // Handle navigation to base route
    if (to.name === "base") {
      // If not authenticated, redirect to login with redirect intent
      if (!authStore.isAuthed) {
        return {
          name: "login",
          query: { redirect: to.fullPath },
        };
      }

      // Wait for profile data to load
      if (profile.isLoading) {
        await new Promise<void>((resolve) => {
          const unwatch = watch(
            () => profile.isLoading,
            (loading) => {
              if (!loading) {
                unwatch();
                resolve();
              }
            },
            { immediate: true },
          );
        });
      }

      return {
        name: homeRouteTargetName(router, {
          isError: profile.isError,
          selectedHomeRoute: profile.data?.selectedHomeRoute,
          hasAgencyHub: profile.data?.hasAgencyHub,
        }),
      };
    }
    return;
  });
}
// Record where each navigation is headed so the stale-chunk recovery can
// reload straight to the target route when its lazy chunk 404s after a deploy,
// instead of reloading the page the user is still on and losing the click.
// Cleared on completion so a later widget/async-import failure reloads in place.
router.beforeEach((to) => {
  recordNavigationTarget(to.fullPath);
});
router.afterEach(() => {
  recordNavigationTarget(null);
});

router.beforeResolve((to) => {
  const shareParam = (to.redirectedFrom?.query.share ?? to.query.share) as
    | string
    | undefined;
  if (shareParam) {
    applyShareParam(shareParam);
  }

  // Clamp any future dates that may have leaked into the global temporal filter
  // (e.g. from dashboard editor allowing future dates for locked date ranges)
  const store = useDataFiltersStore();
  const today = DateTime.local().toISODate()!;
  const hasFutureSelection = store.temporalFilter?.selection?.end > today;
  const hasFutureComparison =
    (store.temporalFilter?.comparison?.end ?? "") > today;
  if (hasFutureSelection || hasFutureComparison) {
    // setTemporalFilter clamps both selection and comparison dates
    store.setTemporalFilter(store.temporalFilter);
  }
});

export default router;

export enum NotFoundErrors {
  ROUTE = "ROUTE",
  INVITE = "INVITE",
  ACCESS_TIER = "ACCESS_TIER",
  OTHER = "OTHER",
}
export function directToNotFound(
  res: NotFoundErrors,
  route: RouteLocationNormalizedLoaded,
): void {
  // https://router.vuejs.org/guide/essentials/dynamic-matching.html#catch-all-404-not-found-route
  router.push({
    name: "not-found",
    // preserve current path and remove the first char to avoid the target URL starting with `//`
    params: { pathMatch: route.path.substring(1).split("/") },
    // preserve existing query and hash if any
    query: { ...route.query, resourceRequested: res },
    hash: route.hash,
  });
}

export function watchRouteForAccessTier(): void {
  // Fires once every route change
  const route = useRoute();
  if (shouldSkipRoute(route)) return;
  const tier = computed(() => {
    return route.meta.requiredTier;
  });
  const organization = useOrganizationQuery();

  watch(
    [() => organization.data, tier],
    () => {
      if (
        tier.value !== undefined &&
        organization.data &&
        !hasAccessTier(organization.data.subscriptionTier, tier.value)
      ) {
        directToNotFound(NotFoundErrors.ACCESS_TIER, route);
      }
    },
    { immediate: true, deep: true },
  );
}
export function watchRouteForUserRole(): void {
  // Fires once every route change
  const route = useRoute();
  if (shouldSkipRoute(route)) return;
  const role = computed(() => {
    return route.meta.requiredUserRole;
  });
  const user = useProfileQuery();

  watch(
    [() => user.data, role],
    () => {
      if (
        role.value !== undefined &&
        user.data &&
        !hasRequiredUserRole(user.data.role, role.value)
      ) {
        directToNotFound(NotFoundErrors.ROUTE, route);
      }
    },
    { immediate: true, deep: true },
  );
}

export function watchRouteForOrganization(): void {
  // Fires once every route change
  const route = useRoute();
  if (shouldSkipRoute(route)) return;
  const routeAccess = useRouteAccess(
    computed(() => route),
    true,
  );

  watchEffect(() => {
    if (!routeAccess.show.value) {
      directToNotFound(NotFoundErrors.ROUTE, route);
    }
  });
}

export function watchRouteForResetFilters(): void {
  const route = useRoute();
  if (shouldSkipRoute(route)) return;
  const dataFiltersStore = useDataFiltersStore();

  watch(
    () => route.meta,
    (newMeta, oldMeta) => {
      if (
        oldMeta &&
        Object.keys(oldMeta).length &&
        (!oldMeta.text || !newMeta.text || oldMeta.text !== newMeta.text)
      ) {
        dataFiltersStore.resetSharableFilters();
      }
    },
    { deep: true, immediate: true },
  );
}

export function watchRouteForFeatureFlag(): void {
  const route = useRoute();
  if (shouldSkipRoute(route)) return;
  const flags = useFeatureFlags();

  watch(
    [() => flags.data, () => flags.isLoading, () => route.meta],
    () => {
      const required = route.meta.requiredFeatureFlag;
      if (!required) return;
      // Only hold off while the flags request is genuinely still loading —
      // don't bounce on initial load. Once it has settled, fail CLOSED: a
      // fetch error (data stays undefined) must block the gated route, not
      // leave it open.
      if (flags.isLoading) return;
      const status = flags.data?.[required];
      if (!status?.enabled) {
        directToNotFound(NotFoundErrors.ROUTE, route);
      }
    },
    { immediate: true, deep: true },
  );
}

export function watchRouteForPermissionRequirement(): void {
  const route = useRoute();
  if (shouldSkipRoute(route)) return;
  const self = useProfileQuery();

  watch(
    [() => self.data, () => route.meta],
    () => {
      if ("requiredPermissions" in route.meta) {
        if (self.data) {
          for (const permission of route.meta.requiredPermissions ?? []) {
            // `isAgency` means "the user belongs to an agency", which is
            // captured by their server-resolved home agency rather than the
            // profile's own boolean flags or the active org. Mirrors
            // Navbar.vue's filteredSettingsRoutes special-case. The
            // agency_hub flag (greyed nav / feed redirect) is handled by
            // useHomeRouteRedirectGuard, not here — this guard only checks
            // membership.
            if (permission === "isAgency") {
              if (!self.data.homeAgency) {
                directToNotFound(NotFoundErrors.ROUTE, route);
                return;
              }
              continue;
            }
            if (
              permission in self.data &&
              !self.data[permission as keyof typeof self.data]
            ) {
              directToNotFound(NotFoundErrors.ROUTE, route);
              return;
            }
          }
        }
      }
    },
    { immediate: true, deep: true },
  );
}

function shouldSkipRoute(route: RouteLocationNormalizedLoadedGeneric): boolean {
  return (
    route === undefined ||
    route.name === undefined ||
    route.name === "shared-dashboard"
  );
}
